import { useState } from "react"
import { Organization, Text2GivePhoneNumber } from "../../types"
import { Fund } from "source/giving/donation_form/types"
import { Button } from "@planningcenter/doxy-web"
import { css } from "@emotion/react"
import {
  PaymentMethodsProvider,
  usePaymentMethodsContext,
} from "../../hooks/usePaymentMethodsContext"
import { Link } from "react-router-dom"
import Text2GiveNotActive from "./Text2GiveNotActive"
import Form from "./Form"
import { AlertMessage } from "source/shared/components"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import DeleteConfirmationDialog from "source/giving/profile/components/DeleteConfirmationDialog"
import usePaymentMethodsUrl from "../../hooks/usePaymentMethodsUrl"

export default function Connected({
  organization,
  funds,
  text2GivePhoneNumber,
  onText2GivePhoneNumberDestroy,
}: {
  organization: Organization
  funds: Fund[]
  text2GivePhoneNumber: Text2GivePhoneNumber
  onText2GivePhoneNumberDestroy: () => void
}) {
  return (
    <PaymentMethodsProvider>
      <ConnectedContent
        organization={organization}
        funds={funds}
        text2GivePhoneNumber={text2GivePhoneNumber}
        onText2GivePhoneNumberDestroy={onText2GivePhoneNumberDestroy}
      />
    </PaymentMethodsProvider>
  )
}

function ConnectedContent({
  organization,
  funds,
  text2GivePhoneNumber,
  onText2GivePhoneNumberDestroy,
}: {
  organization: Organization
  funds: Fund[]
  text2GivePhoneNumber: Text2GivePhoneNumber
  onText2GivePhoneNumberDestroy: () => void
}) {
  const { paymentMethods } = usePaymentMethodsContext()
  const [showDialog, setShowDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasErrors, setHasErrors] = useState(false)

  const paymentMethodsUrl = usePaymentMethodsUrl()

  const destroyText2GivePhoneNumber = async () => {
    setShowDialog(false)
    setIsSaving(true)
    setHasErrors(false)

    try {
      await sessionApiClient.del("/giving/v2/me/text_to_give_phone_number")
      onText2GivePhoneNumberDestroy()
    } catch (error) {
      setHasErrors(true)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <div className="d-f fd-c g-2">
        <div
          className="d-f fd-c g-2 p-2"
          css={css`
            border: 1px solid var(--color-static-gray93);
            border-radius: 8px;
          `}
        >
          {hasErrors && (
            <AlertMessage type="error" icon="general#exclamation-triangle">
              Something went wrong. Please try again.
            </AlertMessage>
          )}
          {!organization.text2give_enabled ||
          !organization.text2give_phone_number ? (
            <Text2GiveNotActive
              organization={organization}
              text2GivePhoneNumber={text2GivePhoneNumber}
            />
          ) : (
            <>
              <div className="d-f fd-c g-2">
                {organization.text2give_phone_number && (
                  <>
                    <p className="mb-1">
                      Text GIVE and the amount you want to give to{" "}
                      <strong>{organization.text2give_phone_number}</strong>.{" "}
                      <Link to="/giving/text-to-give/help" className="fw-500">
                        Learn more
                      </Link>
                      , or{" "}
                      <Link to={`sms:${organization.text2give_phone_number}`}>
                        tap here to start a text to {organization.name}
                      </Link>
                      .
                    </p>
                    <p className="mb-1">
                      Example: send &quot;<strong>GIVE $25</strong>&quot; for a
                      $25 donation to {organization.name}&#39;s {funds[0].name}{" "}
                      fund.
                    </p>
                  </>
                )}
              </div>

              <div className="action-drawer">
                {paymentMethods && paymentMethods.length > 0 ? (
                  <Form
                    text2GivePhoneNumber={text2GivePhoneNumber}
                    organization={organization}
                  />
                ) : (
                  <div>
                    <p>
                      No payment method available for{" "}
                      {text2GivePhoneNumber.phone_number}.
                    </p>
                    {!organization.credit_cards_allowed && (
                      <p>
                        {organization.name} does not accept credit card
                        donations (prepaid gift cards and debit cards are
                        allowed).
                      </p>
                    )}
                    {/* TODO: this should open the payment methods modal instead of navigating to the payment methods page */}
                    <Link
                      to={paymentMethodsUrl}
                      className="btn minor-btn"
                      tabIndex={0}
                    >
                      Add payment method
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div>
          <Button
            text="Disable Text2Give"
            theme="destroy"
            size="md"
            disabled={isSaving}
            onClick={() => setShowDialog(true)}
          />
        </div>
      </div>

      {showDialog && (
        <DeleteConfirmationDialog
          label="Disable Text2Give?"
          description="You will no longer be able to give by sending a text message. This cannot be undone."
          deleteButtonText="Disable Text2Give"
          onCancel={() => setShowDialog(false)}
          onDelete={() => destroyText2GivePhoneNumber()}
        />
      )}
    </>
  )
}
